import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  ArrowLeft, 
  Loader2, 
  AlertCircle,
  CheckCircle,
  MessageCircle,
  FileText,
  Building
} from 'lucide-react';
import { useAuthToken } from '../../components/AuthTokenProvider';
import { sowService } from '../../services/sowService';
import { sowConversationService, SOWConversationResponse } from '../../services/sowConversationService';
import { SOW } from '../../models/sow';
import { WorkItem } from '../../models/workItem';
import { RefineQuestionComponent } from '../../components/RefineQuestionComponent';
import { ImageGallery } from '../../components/ImageGallery';

interface WorkItemRefinementPageProps {}

export const WorkItemRefinementPage: React.FC<WorkItemRefinementPageProps> = () => {
  const { sowId, workItemId, conversationId } = useParams<{ 
    sowId: string; 
    workItemId: string; 
    conversationId?: string; 
  }>();
  const navigate = useNavigate();
  const { tokenSet, isLoading: tokenLoading } = useAuthToken();
  
  // Core data
  const [sow, setSOW] = useState<SOW | null>(null);
  const [workItem, setWorkItem] = useState<WorkItem | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<any>(null);
  
  // Batch state
  const [questionBatch, setQuestionBatch] = useState<any>(null);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null);
  const [batchAnswers, setBatchAnswers] = useState<Array<{ question: any; answer: string }>>([]);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [imageRefreshTrigger, setImageRefreshTrigger] = useState(0);

  useEffect(() => {
    if (sowId && workItemId && tokenSet && !tokenLoading) {
      loadRefinementData();
    }
  }, [sowId, workItemId, conversationId, tokenSet, tokenLoading]);

  const loadRefinementData = async () => {
    if (!sowId || !workItemId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Load SOW and find the work item
      const sowData = await sowService.getSOW(sowId);
      setSOW(sowData);
      
      const targetWorkItem = sowData.workItems.find(item => item.id === workItemId);
      if (!targetWorkItem) {
        setError('Work item not found');
        return;
      }
      setWorkItem(targetWorkItem);
      
      // Check if work item supports refinement
      if (!targetWorkItem.refinementStatus) {
        setError('This work item type does not support refinement');
        return;
      }
      
      // Use the refinementConversationId from the work item
      const conversationId = targetWorkItem.refinementConversationId;
      
      if (conversationId) {
        // Load existing conversation using the work item's conversation ID
        const conversationData = await sowConversationService.getConversation(conversationId);
        if (conversationData.conversationResponse?.outstandingQuestion) {
          setCurrentQuestion(conversationData.conversationResponse.outstandingQuestion);
        }
        // Load batch data from existing conversation
        if ((conversationData.conversationResponse as any)?.questionBatch) {
          setQuestionBatch((conversationData.conversationResponse as any).questionBatch);
        }
        if ((conversationData.conversationResponse as any)?.batchProgress) {
          setBatchProgress((conversationData.conversationResponse as any).batchProgress);
        }
      } else if (targetWorkItem.refinementStatus === 'pending') {
        // Start new refinement conversation
        console.log('Starting refinement with project type:', sowData.scopeDefinition?.projectType);
        console.log('Full scopeDefinition:', sowData.scopeDefinition);
        
        const response = await sowConversationService.startWorkItemRefinement(sowId, workItemId);
        if (response.conversationResponse?.outstandingQuestion) {
          setCurrentQuestion(response.conversationResponse.outstandingQuestion);
        }
        // Store batch data from initial response
        if ((response.conversationResponse as any)?.questionBatch) {
          setQuestionBatch((response.conversationResponse as any).questionBatch);
        }
        if ((response.conversationResponse as any)?.batchProgress) {
          setBatchProgress((response.conversationResponse as any).batchProgress);
        }
        
        // Update URL to include conversation ID
        navigate(`/sow/${sowId}/workitem/${workItemId}/refinement/${response.conversation.id}`, { replace: true });
      }
      
    } catch (error) {
      console.error('Error loading refinement data:', error);
      setError('Failed to load work item refinement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async (userResponse: string) => {
    if (!workItem?.refinementConversationId || !sow) {
      setError('No conversation ID or SOW data found');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Store the current answer
      if (currentQuestion) {
        const newAnswer = { question: currentQuestion, answer: userResponse };
        const updatedAnswers = [...batchAnswers, newAnswer];
        setBatchAnswers(updatedAnswers);
      }
      
      // Check if we have more questions in the current batch
      if (questionBatch && batchProgress && batchProgress.current < batchProgress.total) {
        // Move to next question in the batch locally
        const nextQuestionIndex = batchProgress.current; // current is 1-based, array is 0-based
        const nextQuestion = questionBatch.questions[nextQuestionIndex];
        
        setCurrentQuestion(nextQuestion);
        setBatchProgress({ current: batchProgress.current + 1, total: batchProgress.total });
        
        setSubmitting(false);
        return;
      }
      
      // If we've completed the batch, send all answers to backend
      const allBatchAnswers = [...batchAnswers, { question: currentQuestion, answer: userResponse }];
      const conversationHistory = allBatchAnswers.map(answer => [
        { speaker: 'assistant', text: answer.question.QuestionText },
        { speaker: 'user', text: answer.answer }
      ]).flat();
      
      const response = await sowConversationService.continueWorkItemRefinement(
        workItem.refinementConversationId,
        {
          sowId: sowId,
          workItem: {
            id: workItem.id,
            category: workItem.category,
            description: workItem.description,
            reasoning: workItem.reasoning
          },
          conversation: conversationHistory,
          address: sow.address,
          projectType: sow.scopeDefinition?.projectType,
          propertyData: sow.propertyData
        }
      );
      
      // Update current question from backend response
      if (response.conversationResponse?.outstandingQuestion) {
        setCurrentQuestion(response.conversationResponse.outstandingQuestion);
      }
      
      // Update batch data if new batch is provided
      if ((response.conversationResponse as any)?.questionBatch) {
        setQuestionBatch((response.conversationResponse as any).questionBatch);
        setBatchAnswers([]); // Reset answers for new batch
      }
      if ((response.conversationResponse as any)?.batchProgress) {
        setBatchProgress((response.conversationResponse as any).batchProgress);
      }
      
      // Check if refinement is complete
      if (response.conversationResponse?.refinementComplete) {
        setIsComplete(true);
        setTimeout(() => {
          navigate(`/sow/${sowId}`);
        }, 3000);
      }
      
    } catch (error) {
      console.error('Error submitting response:', error);
      setError('Failed to submit response. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWorkItemImageUpload = async (file: File): Promise<void> => {
    if (!sowId || !workItemId || !workItem?.refinementConversationId || !sow) {
      throw new Error('Required data not found');
    }
    
    try {
      // Upload image (reuse existing upload functionality)
      const imageId = await sowService.uploadImageForAnalysis(sowId, file);
      
      // Associate with work item
      await sowConversationService.associateWorkItemImage(
        sowId, 
        workItemId, 
        imageId, 
        "Photo requested by AI for work item refinement"
      );
      
      // Continue conversation with image - include current batch answers
      const allBatchAnswers = batchAnswers;
      const conversationHistory = allBatchAnswers.map(answer => [
        { speaker: 'assistant', text: answer.question.QuestionText },
        { speaker: 'user', text: answer.answer }
      ]).flat();
      
      // Add the image upload response
      conversationHistory.push({ speaker: 'user', text: 'I uploaded the requested photo' });
      
      const response = await sowConversationService.continueWorkItemRefinement(
        workItem.refinementConversationId, 
        {
          sowId: sowId,
          workItem: {
            id: workItem.id,
            category: workItem.category,
            description: workItem.description,
            reasoning: workItem.reasoning
          },
          conversation: conversationHistory,
          address: sow.address,
          projectType: sow.scopeDefinition?.projectType,
          propertyData: sow.propertyData,
          imageId
        }
      );
      
      // Update current question
      if (response.conversationResponse?.outstandingQuestion) {
        setCurrentQuestion(response.conversationResponse.outstandingQuestion);
      }
      
      // Check if refinement is complete
      if (response.conversationResponse?.refinementComplete) {
        setIsComplete(true);
        setTimeout(() => {
          navigate(`/sow/${sowId}`);
        }, 3000);
      }
      
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleBackToOverview = () => {
    navigate(`/sow/${sowId}`);
  };

  const handleImageUploaded = () => {
    // Trigger image gallery refresh
    setImageRefreshTrigger(prev => prev + 1);
  };

  // Loading state
  if (loading || tokenLoading || !tokenSet) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <div className="text-lg">Loading work item refinement...</div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Unable to Load Refinement</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-x-3">
              <Button onClick={handleBackToOverview}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Overview
              </Button>
              <Button onClick={loadRefinementData} variant="outline">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Completion state
  if (isComplete) {
    return (
      <div className="container mx-auto p-6">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Work Item Refinement Complete!</h3>
            <p className="text-gray-600 mb-4">
              Your work item has been refined with detailed specifications.
            </p>
            <div className="flex items-center justify-center mb-6">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              <span>Redirecting back to overview...</span>
            </div>
            <Button onClick={handleBackToOverview} variant="outline">
              Return to Overview
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={handleBackToOverview}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Overview
        </Button>
        
        <div className="flex items-center gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold">Refine Work Item</h1>
            <div className="flex items-center gap-2 text-gray-600">
              <Building className="h-4 w-4" />
              <span>{sow?.scopeDefinition?.projectType}</span>
              <span>•</span>
              <FileText className="h-4 w-4" />
              <span>{sow?.address}</span>
            </div>
          </div>
        </div>

        {/* Work Item Info */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-1">{workItem?.description}</h3>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="outline" className="text-xs">
                  {workItem?.type === 'RENOVATION' ? 'Renovation Item' : 'Repair Item'}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {workItem?.refinementStatus === 'pending' ? 'Needs Refinement' :
                   workItem?.refinementStatus === 'in_progress' ? 'Refining...' :
                   'Ready for Estimation'}
                </Badge>
                {batchProgress && (
                  <Badge variant="outline" className="text-xs">
                    Question {batchProgress.current} of {batchProgress.total}
                  </Badge>
                )}
              </div>
              {/* Progress Bar */}
              {batchProgress && (
                <div className="space-y-1">
                  <div className="text-xs text-blue-700 font-medium">
                    Progress: {batchProgress.current} of {batchProgress.total} questions
                  </div>
                  <Progress 
                    value={((batchProgress.current - 1) / batchProgress.total) * 100} 
                    max={100}
                    className="h-2 bg-blue-100"
                  />
                </div>
              )}
            </div>
            <MessageCircle className="h-6 w-6 text-blue-600 ml-4" />
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {/* Current Question */}
          {currentQuestion && (
            <RefineQuestionComponent
              question={currentQuestion}
              onContinue={handleContinue}
              onImageUpload={handleWorkItemImageUpload}
              onImageUploaded={handleImageUploaded}
              isSubmitting={submitting}
            />
          )}
        </div>

        {/* Sidebar - Image Gallery */}
        <div className="lg:col-span-1">
          <ImageGallery 
            sowId={sowId!} 
            className="sticky top-6"
            refreshTrigger={imageRefreshTrigger}
          />
        </div>
      </div>
    </div>
  );
};